import axios from 'axios';
import { LogiGroup, LogiUser } from './logi.types';

const ACCEPT_HEADER = 'application/vnd.composer.v3+json';

// The import throws error with decrypt toString conversion so using require for now (and it works as expected)
// eslint-disable-next-line @typescript-eslint/no-var-requires
const CryptoJS = require('crypto-js');

const decryptText = (encryptedText: string): string => {
  const byteText = CryptoJS.AES.decrypt(
    encryptedText,
    'This is Awesome Dashboard'
  );
  return byteText.toString(CryptoJS.enc.Utf8);
};

export async function getLogiVersion(externalDashboardIP: string) {
  const versionUrl = `${externalDashboardIP}/composer/api/version`;
  const {
    data: { version }
  } = await axios.get(versionUrl);
  console.log(`Detected Logi version: ${version}`);
  return version;
}

export async function getUserGroups(
  externalDashboardIP: string,
  logiAdminUserName: string,
  logiAdminPswd: string
): Promise<{ [label: string]: LogiGroup }> {
  const url = `${externalDashboardIP}/composer/api/groups?limit=-1&offset=0`;
  const { data: groups } = await axios.get(url, {
    headers: {
      Accept: ACCEPT_HEADER
    },
    auth: {
      username: decryptText(logiAdminUserName),
      password: decryptText(logiAdminPswd)
    }
  });

  return groups.content.reduce(
    (acc: { [label: string]: LogiGroup }, curr: LogiGroup) => {
      acc[curr.label] = curr;
      return acc;
    },
    {} as { [label: string]: LogiGroup }
  );
}

export async function getLogiUsers(
  externalDashboardIP: string,
  logiSupervisorUserName: string,
  logiSupervisorPswd: string
): Promise<{ id: string; name: string }[]> {
  const logiUsersUrl = `${externalDashboardIP}/composer/api/users?limit=-1&offset=0`;
  const { data: users } = await axios.get(logiUsersUrl, {
    headers: {
      Accept: ACCEPT_HEADER
    },
    auth: {
      username: decryptText(logiSupervisorUserName),
      password: decryptText(logiSupervisorPswd)
    }
  });

  return users.content || [];
}

export async function addLogiUserToUserGroup(
  externalDashboardIP: string,
  logiAdminUserName: string,
  logiAdminPswd: string,
  logiAdminAccount: string,
  userGroupId: string,
  userGroupLabel: string,
  logiUserId: string
) {
  await axios.put(
    `${externalDashboardIP}/composer/api/groups`,
    {},
    {
      headers: {
        Accept: ACCEPT_HEADER,
        'Content-Type': ACCEPT_HEADER
      },
      auth: {
        username: decryptText(logiAdminUserName),
        password: decryptText(logiAdminPswd)
      },
      data: {
        group: {
          id: userGroupId,
          accountId: logiAdminAccount,
          label: userGroupLabel,
          description: '',
          roles: [
            'ROLE_EXPORT_DASHBOARDS',
            'ROLE_EDIT_FORMULAS',
            'ROLE_SAVE_FILTERS',
            'ROLE_CREATE_DASHBOARDS'
          ],
          system: false
          // createdByUserID: '6654299279463b3ba7ee20bf',
          // lastModifiedByUserID: '6654299279463b3ba7ee20bf',
          // createdDate: '2024-06-21 20:31:38.291',
          // lastModifiedDate: '2024-06-25 16:52:20.675'
        },
        sources: [],
        userIds: [logiUserId]
      }
    }
  );
}

export async function createLogiUser(
  externalDashboardIP: string,
  logiAdminUserName: string,
  logiAdminPswd: string,
  logiAdminAccount: string,
  user: string
) {
  const { data: logiUser } = await axios.post(
    `${externalDashboardIP}/composer/api/users`,
    {},
    {
      headers: {
        Accept: ACCEPT_HEADER,
        'Content-Type': ACCEPT_HEADER
      },
      auth: {
        username: decryptText(logiAdminUserName),
        password: decryptText(logiAdminPswd)
      },
      data: {
        fullname: user,
        accountId: logiAdminAccount,
        accounts: [
          {
            accountId: logiAdminAccount,
            groups: [],
            roles: [],
            userAttributes: [],
            disabled: false,
            accountName: 'Visual Data Discovery',
            accountDisabled: false
          }
        ],
        name: user,
        groupRoles: [],
        forcePasswordChange: false,
        showQuickstart: false,
        userOrigin: 'NATIVE',
        external: false,
        localeSettingsId: 'en_US',
        languageLocaleId: 'en_US',
        timeZone: 'UTC',
        deletable: true,
        availableAttributeKeys: [
          'accountId',
          'credentials',
          'timeZone',
          'composerUserName'
        ],
        activeGroups: [],
        activeRoles: [
          'ROLE_EXPORT_DASHBOARDS',
          'ROLE_EDIT_FORMULAS',
          'ROLE_SAVE_FILTERS',
          'ROLE_EXPORT_VISUALS',
          'ROLE_CREATE_DASHBOARDS',
          'ROLE_PERMISSION_VISUALS'
        ],
        version: 2
      }
    }
  );
  return logiUser;
}

export async function updateLogiUser(
  externalDashboardIP: string,
  logiAdminUserName: string,
  logiAdminPswd: string,
  logiAdminAccount: string,
  logiUserId: string,
  user: string,
  groupIds: string[],
  password: string,
  version: string
) {
  await axios.put(
    `${externalDashboardIP}/composer/api/users/${logiUserId}`,
    {},
    {
      headers: {
        Accept: ACCEPT_HEADER,
        'Content-Type': ACCEPT_HEADER
      },
      auth: {
        username: decryptText(logiAdminUserName),
        password: decryptText(logiAdminPswd)
      },
      data: {
        accountId: logiAdminAccount,
        accounts: [
          {
            accountDisabled: false,
            accountId: logiAdminAccount,
            accountName: 'Visual Data Discovery',
            groups: groupIds,
            disabled: false,
            roles: [
              'ROLE_EXPORT_DASHBOARDS',
              'ROLE_EDIT_FORMULAS',
              'ROLE_SAVE_FILTERS',
              'ROLE_EXPORT_VISUALS',
              'ROLE_CREATE_DASHBOARDS',
              'ROLE_PERMISSION_VISUALS'
            ],
            userAttributes: []
          }
        ],
        activeGroups: groupIds,
        activeRoles: [
          'ROLE_EXPORT_DASHBOARDS',
          'ROLE_EDIT_FORMULAS',
          'ROLE_SAVE_FILTERS',
          'ROLE_EXPORT_VISUALS',
          'ROLE_CREATE_DASHBOARDS',
          'ROLE_PERMISSION_VISUALS'
        ],
        availableAttributeKeys: [],
        deletable: false,
        external: false,
        forcePasswordChange: false,
        fullname: user,
        languageLocaleId: 'en_US',
        localeSettingsId: 'en_US',
        name: user,
        password,
        showQuickstart: false,
        version
      }
    }
  );
}

export async function getUserByUserId(
  externalDashboardIP: string,
  logiAdminUserName: string,
  logiAdminPswd: string,
  logiUserId: string
) {
  const { data: user } = await axios.get(
    `${externalDashboardIP}/composer/api/users/${logiUserId}`,
    {
      headers: {
        Accept: ACCEPT_HEADER
      },
      auth: {
        username: decryptText(logiAdminUserName),
        password: decryptText(logiAdminPswd)
      }
    }
  );
  return user;
}

export function isLogiUserMissingRoles(logiUser: LogiUser): boolean {
  if (!logiUser.activeRoles) {
    return true;
  }

  const requiredRoles = [
    'ROLE_EXPORT_DASHBOARDS',
    'ROLE_EDIT_FORMULAS',
    'ROLE_SAVE_FILTERS',
    'ROLE_CREATE_DASHBOARDS'
  ];

  return !requiredRoles.every((role) => logiUser.activeRoles.includes(role));
}

export function isLogiUserMissingGroups(logiUser: LogiUser, group: LogiGroup): boolean {
  return (logiUser.activeGroups.some((gid) => group.id === gid));
}
